import { ArrowLeftIcon, CalendarEditIcon, PencilLineIcon, ClockIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { Button } from '@repo/ui';
import { formatDuration, formatDate } from '@repo/date-time';

import {
  selectParkDuration,
  selectParkStartAtTimestamp,
  useParkingSearchStore,
} from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  testId: TestId;
}

export default function DurationBar({ testId }: Props) {
  const navigate = useNavigate();
  const parkDuration = useParkingSearchStore(selectParkDuration);
  const parkStartAtTimestamp = useParkingSearchStore(selectParkStartAtTimestamp);
  const parkStartLabel = useMemo(
    () =>
      parkStartAtTimestamp !== undefined
        ? `${tr('parking_search:park_start.since')} ${formatDate(new Date(parkStartAtTimestamp), true)}`
        : tr('parking_search:park_start.now'),
    [parkStartAtTimestamp]
  );
  const isParkingDetailsRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.PARKING_DETAILS]));

  const handleBack = () => {
    if (isParkingDetailsRoute) {
      navigate(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]);
    } else {
      navigate(ROUTE_PATHS[RouteName.MAP]);
    }
  };

  const setIsTimeContractPickerModalOpen = useParkingSearchStore((state) => state.setIsTimeContractPickerModalOpen);
  const setIsParkStartDrawerOpen = useParkingSearchStore((state) => state.setIsParkStartDrawerOpen);

  const handleEditTime = () => {
    setIsTimeContractPickerModalOpen(true);
  };

  const handleEditParkStart = () => {
    setIsParkStartDrawerOpen(true);
  };

  return (
    <div className="bg-primary border-stroke-base-medium z-floating absolute top-4 right-4 left-4 flex max-w-100 items-center gap-1.5 rounded-xl border p-2 shadow-lg sm:mx-auto sm:mr-auto md:hidden">
      <Button
        iconOnly
        size="sm"
        color="tertiary"
        leftIcon={ArrowLeftIcon}
        onClick={handleBack}
        testId={testId.append('backButton')}
      />
      <Button
        size="lg"
        color="secondary"
        onClick={handleEditParkStart}
        className="min-w-0 flex-1 justify-between! px-3!"
        testId={testId.append('parkStartButton')}
        rightIcon={PencilLineIcon}
      >
        <div className="flex min-w-0 items-center gap-2">
          <CalendarEditIcon size={14} className="text-body shrink-0" />
          <span className="text-header truncate text-base font-medium">{parkStartLabel}</span>
        </div>
      </Button>
      <Button
        onClick={handleEditTime}
        size="lg"
        testId={testId.append('timeDisplay')}
        color="secondary"
        className="min-w-0 flex-1 justify-between! px-3!"
        rightIcon={PencilLineIcon}
      >
        <div className="flex min-w-0 items-center gap-2">
          <ClockIcon size={14} className="text-body shrink-0" />
          <span className="text-header truncate text-base font-medium">{formatDuration(parkDuration)}</span>
        </div>
      </Button>
    </div>
  );
}
